import { SearchAndFilterDisplayOptionsModel } from 'models';

import { LodgingPwaPropertySearch } from '__generated__/api';
import { PillProps } from 'components/search/playback/playback-filter-pill';

const validFields = [
  'sort',
  'hotelName',
  'commissionTier',
  'agencyBusinessModel',
  'rewards',
  'popularFilter',
  'star',
  'price',
  'guestRating',
  'cleaningAndSafetyPractices',
  'paymentType',
  'propertyStyle',
  'lodging',
  'bedroomFilter',
  'regionId',
  'neighborhood',
  'poi',
  'mealPlan',
  'amenities',
  'accessibility',
  'travelerType',
  'deals',
  'stayType',
];

export const createSearchAndFilterDisplayOptions = (
  sortAndFilter?: LodgingPwaPropertySearch.sortAndFilter
): SearchAndFilterDisplayOptionsModel => {
  if (!sortAndFilter) {
    return {
      filters: {
        displayFields: validFields,
      },
    };
  }

  return {
    filters: {
      displayFields: sortAndFilter.options?.sortAndFilter.filter(option => validFields.includes(option.title)).map(option => option.title),
    },
  };
};

export enum SortAndFilterDisplayField {
  SORT = 'sort',
  HOTEL_NAME = 'hotelName',
  REWARDS = 'rewards',
  STAR = 'star',
  PRICE = 'price',
  GUEST_RATING = 'guestRating',
  CLEANING_AND_SAFETY = 'cleaningAndSafetyPractices',
  LODGING = 'lodging',
  REGION_ID = 'regionId',
  MEAL_PLAN = 'mealPlan',
  AMENITIES = 'amenities',
  TRAVELER_TYPE = 'travelerType',
  PAYMENT_TYPE = 'paymentType',
  DEALS = 'deals',
  PROPERTY_STYLE = 'propertyStyle',
  BEDROOM_FILTER = 'bedroomFilter',
  NEIGHBORHOOD = 'neighborhood',
  STAY_TYPE = 'stayType',
}

export const mapSortAndFilter = (sortAndFilter: LodgingPwaPropertySearch.sortAndFilter): any =>
  sortAndFilter
    ? {
        defaults: getDefaults(sortAndFilter),
        options: getOptions(sortAndFilter),
        pills: getPills(sortAndFilter),
      }
    : null;

export const getSortAndFilterOption = (
  field: SortAndFilterDisplayField,
  sortAndFilter: any
): LodgingPwaPropertySearch._sortAndFilter | null => {
  if (sortAndFilter && sortAndFilter.options) {
    const result = sortAndFilter.options[field];

    return result ? result : null;
  }

  return null;
};

const getDefaults = (sortAndFilter: LodgingPwaPropertySearch.sortAndFilter): any => ({
  neighborhood: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.regionId,
  sort: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.sort,
});

const getOptions = (sortAndFilter: LodgingPwaPropertySearch.sortAndFilter): any => {
  const appliedFilterCount = sortAndFilter && sortAndFilter.options && sortAndFilter.options.appliedFilterCount;
  const sortAndFilterOptions = sortAndFilter && sortAndFilter.options && mapSortAndFilterOptions(sortAndFilter.options.sortAndFilter);
  const accessibilityLabel = sortAndFilter && sortAndFilter.options && sortAndFilter.options.accessibilityLabel;

  return Object.assign({}, { appliedFilterCount }, sortAndFilterOptions, { accessibilityLabel });
};

const mapSortAndFilterOptions = (sortAndFilterOptions: LodgingPwaPropertySearch._sortAndFilter[]): any =>
  Object.assign(
    {},
    ...sortAndFilterOptions.map(sortAndFilterOption => ({
      [sortAndFilterOption.title]: {
        options: sortAndFilterOption.options.map(mapOption),
        label: sortAndFilterOption.label,
        max: sortAndFilterOption.max,
        min: sortAndFilterOption.min,
        range: sortAndFilterOption.isRange,
        placeholder: sortAndFilterOption.placeholder,
        subLabel: sortAndFilterOption.subLabel,
        step: sortAndFilterOption.step,
        name: sortAndFilterOption.name,
        hiddenThreshold: sortAndFilterOption.hiddenThreshold,
        title: sortAndFilterOption.title,
        showListOptions: sortAndFilterOption.showListOptions,
        dropdownOptions: sortAndFilterOption.dropdownOptions,
      },
    }))
  );

const mapOption = (option: LodgingPwaPropertySearch._options): any => ({
  selected: option.isSelected,
  ...option,
  value: option.optionValue,
});

const getPills = (filters: LodgingPwaPropertySearch.sortAndFilter): PillProps[] => {
  const selectedFilters = filters?.options?.sortAndFilter?.filter(filter => filter.selected);

  if (!selectedFilters) return [];

  return selectedFilters
    .map((filter: LodgingPwaPropertySearch._sortAndFilter) =>
      filter.options.filter(option => option.isSelected).map(option => getPill(filter, option))
    )
    .reduce((previousValue, currentValue) => previousValue.concat(currentValue), [] as PillProps[]);
};

const getPill = (filter: LodgingPwaPropertySearch._sortAndFilter, option: LodgingPwaPropertySearch._options): PillProps => ({
  query: '',
  name: filter.title,
  label: option.selectedLabel || '',
  value: option.optionValue,
  generated: true,
});
